import { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, MapPin, Phone, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/motion/Reveal";

const inputCls =
  "w-full h-11 px-4 bg-transparent border border-[#C6A87C]/25 text-[#F5F4F0] placeholder:text-[#8A99AA]/50 focus:border-[#C6A87C] outline-none transition-colors duration-300 text-sm";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll reply within one business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div data-testid="contact-page" className="bg-navy">
      <PageHero
        testid="contact-hero"
        overline="Get in touch"
        lines={[
          <>Questions before</>,
          <>
            you <em className="italic text-[#C6A87C]">enrol?</em>
          </>,
        ]}
        sub="Write to us about your requirements — whether it is a Patentability Search, Patent Landscape Analysis, Patent Invalidity Search, FTO Study, IP Strategy, etc. We reply within one business day."
      />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-20 lg:py-28 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-4 space-y-9">
          <Reveal>
            <Detail icon={EnvelopeSimple} k="Email" v="venkatvasu@hotmail.com" />
          </Reveal>
          <Reveal delay={0.1}>
            <Detail icon={Phone} k="Phone" v="+91-9819998036" />
          </Reveal>
          <Reveal delay={0.2}>
            <Detail icon={MapPin} k="Office" v="Bangalore, India" />
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-navy-deep border border-[#C6A87C]/20 p-8 lg:p-10 space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <input data-testid="contact-name" value={form.name} onChange={update("name")} className={inputCls} />
                </Field>
                <Field label="Email" required>
                  <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className={inputCls} />
                </Field>
                <Field label="Phone" required>
                  <input data-testid="contact-phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="+91 ..." className={inputCls} />
                </Field>
                <Field label="Subject">
                  <input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className={inputCls} />
                </Field>
              </div>
              <Field label="Message" required>
                <textarea
                  data-testid="contact-message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className={`${inputCls} h-auto py-3 resize-y`}
                />
              </Field>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={sending}
                className="group relative inline-flex items-center gap-3 h-12 px-10 bg-[#C6A87C] text-[#061324] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden disabled:opacity-50"
              >
                <span className="absolute inset-0 bg-[#E2C69D] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                <span className="relative inline-flex items-center gap-2">
                  {sending ? "Sending..." : "Send message"}
                  <ArrowRight size={14} weight="bold" />
                </span>
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#8A99AA] font-semibold">
        {label} {required && <span className="text-[#C6A87C]">*</span>}
      </label>
      {children}
    </div>
  );
}

function Detail({ icon: Icon, k, v }) {
  return (
    <div className="flex gap-4 items-center group">
      <span className="w-11 h-11 border border-[#C6A87C]/30 flex items-center justify-center text-[#C6A87C] transition-colors duration-300 group-hover:bg-[#C6A87C] group-hover:text-[#061324]">
        <Icon size={18} weight="light" />
      </span>
      <div>
        <div className="label-overline !text-[10px]">{k}</div>
        <div className="text-[#F5F4F0] font-medium mt-1">{v}</div>
      </div>
    </div>
  );
}
